import React, { useState } from 'react';

import axios from 'axios';
import {
  HiOutlineMail,
  HiOutlinePhone,
  HiOutlineUser,
  HiOutlineChatAlt2,
} from 'react-icons/hi';
import { HiCheckCircle, HiExclamationCircle } from 'react-icons/hi2';

import { IUserData } from '@/modules/core/types';

interface FormData {
  nom: string;
  numeroTelephone: string;
  email: string;
  message: string;
}

interface ContactFormProps {
  adid: string;
  promoteur: string;
  title: string;
  catId?: string;
  userDetails?: IUserData;
}

interface InputFieldProps {
  name: keyof FormData;
  type: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  placeholder: string;
  required?: boolean;
  formData: FormData;
  errors: Partial<FormData>;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
}

const InputField: React.FC<InputFieldProps> = ({
  name,
  type,
  label,
  icon: Icon,
  placeholder,
  required = true,
  formData,
  errors,
  handleInputChange,
  handleBlur,
}) => (
  <div className="mb-4">
    <label
      htmlFor={name}
      className="block text-sm font-medium text-gray-700 mb-2"
    >
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
    </label>
    <div className="relative">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <Icon className="h-5 w-5 text-gray-400" />
      </div>
      <input
        type={type}
        name={name}
        id={name}
        value={formData[name]}
        onChange={handleInputChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 ${
          errors[name]
            ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
            : 'border-gray-300 bg-white hover:border-gray-400'
        }`}
        required={required}
      />
    </div>
    {errors[name] && (
      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
        <HiExclamationCircle className="h-4 w-4" />
        {errors[name]}
      </p>
    )}
  </div>
);

const ContactForm: React.FC<ContactFormProps> = ({
  adid,
  promoteur,
  title,
  catId,
  userDetails,
}) => {
  const [formData, setFormData] = useState<FormData>({
    nom: '',
    numeroTelephone: '',
    email: '',
    message: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formSuccess, setFormSuccess] = useState<boolean | null>(null);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'nom': {
        if (!value.trim()) return 'Le nom est requis';
        if (value.length < 2)
          return 'Le nom doit contenir au moins 2 caractères';
        return '';
      }
      case 'email': {
        if (!value.trim()) return 'L&apos;email est requis';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return "Format d'email invalide";
        return '';
      }
      case 'numeroTelephone': {
        if (!value.trim()) return 'Le numéro de téléphone est requis';
        const phoneRegex = /^[+]?[\d\s-()]{8,}$/;
        if (!phoneRegex.test(value)) return 'Format de téléphone invalide';
        return '';
      }
      case 'message': {
        // No validation required for message - it's now optional
        if (value.trim() && value.length < 10)
          return 'Le message doit contenir au moins 10 caractères';
        return '';
      }
      default:
        return '';
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    let isValid = true;

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof FormData]);
      if (error) {
        newErrors[key as keyof FormData] = error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setFormSuccess(null);

    try {
      const columns = [
        title,
        adid,
        promoteur,
        formData.nom,
        formData.numeroTelephone,
        formData.email,
        formData.message,
      ];

      let filedId = '';
      console.log('catId:', catId);

      switch (catId) {
        case 'immoneuf': {
          filedId = '1nw6OhmidMNQerWG99NJl8mgMs-snqqiiFfNrNkVWA8s';
          break;
        }
        case 'véhicules': {
          filedId = '1JNCSGXH_9tba6fXRl1M-j1nxbXz1UOq4nq_U6O4oKVY';
          break;
        }
        case 'immobilier': {
          filedId = '17k6mw9ZKw0BFYM-smetU-kV5dyEgYX2Uu1qGfQe_9mY';
          break;
        }
        default: {
          filedId = '1nw6OhmidMNQerWG99NJl8mgMs-snqqiiFfNrNkVWA8s';
          break;
        }
      }

      if (userDetails?.kcid === '66d595fc801a64bd44e9dcfd') {
        filedId = '1PmOk-WWtHCwjHonYEqacY6CAAiu0eGbTX2uO7vVsHkg';
      }

      const response = await axios.post('/api/spreadwriter', {
        fileId: filedId,
        columns,
        catId,
        userDetails,
        NotifyByMail: true,
      });

      if (response.status === 200) {
        setFormSuccess(true);
        setFormData({ nom: '', numeroTelephone: '', email: '', message: '' });
        setErrors({});

        // Reset success message after 5 seconds
        setTimeout(() => {
          setFormSuccess(null);
        }, 5000);
      } else {
        setFormSuccess(false);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error submitting form:', error);
      setFormSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (formSuccess === true) {
    return (
      <div className="w-full  rounded-lg">
        <div className="text-center py-8">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <HiCheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Message envoyé avec succès !
          </h3>
          <p className="text-gray-600">
            {promoteur} vous contactera prochainement pour répondre à votre
            demande.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full ">
      <form onSubmit={handleSubmit} className="space-y-1">
        <InputField
          name="nom"
          type="text"
          label="Nom et prénom"
          icon={HiOutlineUser}
          placeholder="Entrez votre nom complet"
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
        />

        <InputField
          name="email"
          type="email"
          label="Adresse email"
          icon={HiOutlineMail}
          placeholder="votre@email.com"
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
        />

        <InputField
          name="numeroTelephone"
          type="tel"
          label="Numéro de téléphone"
          icon={HiOutlinePhone}
          placeholder="+216 XX XXX XXX"
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
          handleBlur={handleBlur}
        />

        <div className="mb-6">
          <label
            htmlFor="message"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Votre message (optionnel)
          </label>
          <div className="relative">
            <div className="absolute top-3 left-3 pointer-events-none">
              <HiOutlineChatAlt2 className="h-5 w-5 text-gray-400" />
            </div>
            <textarea
              name="message"
              id="message"
              value={formData.message}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="Décrivez votre projet ou posez vos questions..."
              rows={4}
              className={`w-full pl-10 pr-4 py-3 border rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-none ${
                errors.message
                  ? 'border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500/20'
                  : 'border-gray-300 bg-white hover:border-gray-400'
              }`}
            />
          </div>
          {errors.message && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <HiExclamationCircle className="h-4 w-4" />
              {errors.message}
            </p>
          )}
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={isLoading}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500 transform '
            }`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Envoi en cours...
              </div>
            ) : (
              'Envoyer ma demande'
            )}
          </button>
        </div>

        {formSuccess === false && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <HiExclamationCircle className="h-5 w-5" />
              <p className="text-sm font-medium">
                Erreur lors de l&apos;envoi du message. Veuillez réessayer.
              </p>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;
